import { Form } from 'antd';
import PhoneNumber from 'awesome-phonenumber';
import Button from 'components/ui/base/button';
import { FormItem } from 'components/ui/base/form';
import FormInput from 'components/ui/composed/formInput/FormInput';
import FormPhoneNumber from 'components/ui/composed/formPhoneNumber/FormPhoneNumber';
import FormSelect from 'components/ui/composed/formSelect/FormSelect';
import { useFormik } from 'formik';
import useLoadingButton from 'hooks/useLoadingButton';
import useSession from 'hooks/useSession';
import { FC, useEffect, useMemo, useState } from 'react';
import { createIndividual, editIndividual } from 'store/individuals/client';
import { Individual } from 'store/individuals/individual';
import CustomerAddressForm from 'views/customers/components/CustomerAddressForm';
import { getIndividualSchema } from '../individualSchema';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import TeamSelectField from 'views/templates/components/TeamSelectField';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { TEAM_ROLES } from 'config/roles/roles';

interface Props {
    closeDrawer: () => void;
    individual?: Individual;
    registeredUsers?: Individual[];
    onSuccess?: () => void;
    roleSelectOptions?: any;
}

const CreateNewIndividual: FC<Props> = ({ closeDrawer, individual, registeredUsers, onSuccess, roleSelectOptions }) => {
    const { userInfo } = useSession();

    const [showTeamSelection, setShowTeamSelection] = useState(false);

    const validationSchema = useMemo(() => {
        return getIndividualSchema({
            registeredUsers,
            editingUser: individual,
            withTeamId: showTeamSelection,
        });
    }, [individual, registeredUsers, showTeamSelection]);

    const {
        values,
        getFieldProps,
        resetForm,
        getFieldMeta,
        submitForm,
        setValues,
        isValid,
        setFieldValue,
        getFieldHelpers,
        setFieldTouched,
        setFieldError,
    } = useFormik<Partial<Individual & { phoneNumber?: string; phoneCountryCode?: string; teamId?: string }>>({
        initialValues: {},
        validationSchema,
        validateOnMount: true,
        enableReinitialize: true,
        validateOnChange: false,
        onSubmit: (values) => createNewIndividualSubmit(values),
    });
    const { loading, handleOnPressButton } = useLoadingButton({ onSubmit: submitForm });

    const createNewIndividualSubmit = async (
        values: Partial<Individual & { phoneCountryCode?: string; phoneNumber?: string; teamId?: string }>
    ) => {
        let phone;

        if (values?.phoneCountryCode && values?.phoneNumber) {
            // Remove non-number symbols from the number (e.g. 222 123 456 | 222-123-456 -> 222123456)
            const normalizedNumber = values.phoneNumber.replace(/\D/g, '');
            phone = `${values.phoneCountryCode}${normalizedNumber}`;
        }

        const body = {
            fullName: values.fullName,
            address: values.address,
            phone: phone,
            email: values.email,
            type: values.type,
            isInvited: true,
            customerId: userInfo?.customerId,
            teamId: values?.teamId,
            active: true,
        };

        if (individual && individual.id) {
            editIndividual(individual.id, body)
                .then(() => {
                    Notification({
                        type: 'success',
                        messageTitle: 'User Updated',
                        description: 'Successfully updated user',
                        placement: 'top',
                    });
                    onSuccess?.();
                    closeDrawer();
                })
                .catch((err) => {
                    const data = err?.response?.data;

                    // We receve some backend validation errors
                    // Try to populate the form with the BE errors
                    if (data?.subErrors?.length) {
                        data.subErrors.forEach((backendError: any) => {
                            const fieldPath = backendError?.field;
                            const message = backendError?.message;

                            if (fieldPath && message) {
                                setFieldTouched(fieldPath, true, false);
                                setFieldError(fieldPath, message);
                            }
                        });
                    }

                    const errorMessage = getErrorMessage(err);
                    Notification({
                        type: 'error',
                        messageTitle: 'Update Error',
                        description: errorMessage || 'Failed to edit user.',
                        placement: 'top',
                    });
                });
        } else {
            createIndividual(body)
                .then(() => {
                    Notification({
                        type: 'success',
                        messageTitle: 'User Created',
                        description: 'Successfully created user',
                        placement: 'top',
                    });
                    onSuccess?.();
                    resetForm();
                    closeDrawer();
                })
                .catch((err) => {
                    const data = err?.response?.data;

                    // We receve some backend validation errors
                    // Try to populate the form with the BE errors
                    if (data?.subErrors?.length) {
                        data.subErrors.forEach((backendError: any) => {
                            const fieldPath = backendError?.field;
                            const message = backendError?.message;

                            if (fieldPath && message) {
                                setFieldTouched(fieldPath, true, false);
                                setFieldError(fieldPath, message);
                            }
                        });
                    }

                    const errorMessage = getErrorMessage(err);
                    Notification({
                        type: 'error',
                        messageTitle: 'Creation Error',
                        description: errorMessage || 'Could not create user.',
                        placement: 'top',
                    });
                });
        }
    };

    // Populate the initial values of the form
    useEffect(() => {
        if (individual) {
            // Note: The BE currently restricts an individual to a single team membership
            const teamId = individual.teamMemberships?.[0]?.teamId;
            setValues({ ...individual, teamId });
            if (individual.phone) {
                const phone = new PhoneNumber(individual.phone);
                const data = {
                    ...individual,
                    teamId,
                    phoneCountryCode: `+${phone.getCountryCode().toString()}`,
                    phoneNumber: phone.getNumber('significant'),
                };
                setValues(data);
            }
        } else {
            setValues({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [individual]);

    // Display a team selection when the selected user role is a team role
    useEffect(() => {
        if (!values.type) {
            setShowTeamSelection(false);
            setFieldValue('teamId', undefined);
        } else {
            const isTeamRole = TEAM_ROLES.includes(values.type);
            setShowTeamSelection(isTeamRole);
        }
    }, [setFieldValue, values.type]);

    const onCloseButton = () => {
        closeDrawer();
        resetForm();
    };

    const emailDisabled = useMemo(() => {
        return !!individual && !!individual.id;
    }, [individual]);

    return (
        <Form layout="vertical">
            <FormInput
                required
                label="Full name"
                placeholder="Enter User Name"
                fieldMeta={getFieldMeta('fullName')}
                fieldProps={getFieldProps('fullName')}
            />
            <FormInput
                disabled={emailDisabled}
                required
                label="Email Address"
                placeholder="Enter Email Address"
                fieldMeta={getFieldMeta('email')}
                fieldProps={getFieldProps('email')}
            />

            {emailDisabled && (
                <FormInput
                    disabled={true}
                    label="Registered"
                    fieldMeta={getFieldMeta('registered')}
                    fieldProps={{
                        ...getFieldProps('registered'),
                        value: individual?.registered ? 'Yes' : 'No',
                    }}
                />
            )}

            <FormSelect
                required
                label="Role"
                hideKeys
                disabled={individual?.id === userInfo?.individualId}
                placeholder="Select Role"
                fieldMeta={getFieldMeta('type')}
                fieldProps={getFieldProps('type')}
                fieldHelper={getFieldHelpers('type')}
                selectOptions={roleSelectOptions}
            />

            {showTeamSelection && (
                <div style={{ marginBottom: 16 }}>
                    <div>
                        Team <LabelRequiredSymbol style={{ marginRight: 8 }} />
                    </div>
                    <TeamSelectField
                        size="large"
                        fieldHelper={getFieldHelpers('teamId')}
                        fieldMeta={getFieldMeta('teamId')}
                        fieldProps={getFieldProps('teamId')}
                    />
                </div>
            )}

            <CustomerAddressForm
                fieldProps={{
                    addressLine1: getFieldProps('address.addressLine1'),
                    addressLine2: getFieldProps('address.addressLine2'),
                    postCode: getFieldProps('address.postCode'),
                    city: getFieldProps('address.city'),
                    country: getFieldProps('address.country'),
                }}
                fieldMeta={{
                    addressLine1: getFieldMeta('address.addressLine1'),
                    addressLine2: getFieldMeta('address.addressLine2'),
                    postCode: getFieldMeta('address.postCode'),
                    city: getFieldMeta('address.city'),
                    country: getFieldMeta('address.country'),
                }}
                fieldHelper={{
                    addressLine1: getFieldHelpers('address.addressLine1'),
                    addressLine2: getFieldHelpers('address.addressLine2'),
                    postCode: getFieldHelpers('address.postCode'),
                    city: getFieldHelpers('address.city'),
                    country: getFieldHelpers('address.country'),
                }}
                label={{
                    addressLine1: 'Address Line 1',
                    addressLine2: 'Address Line 2',
                    postCode: 'Post Code',
                    city: 'City',
                    country: 'Country',
                }}
                required={{
                    addressLine1: true,
                    addressLine2: false,
                    postCode: true,
                    city: true,
                    country: true,
                }}
            />

            <FormPhoneNumber
                fieldCodeProps={getFieldProps('phoneCountryCode')}
                fieldCodeMeta={getFieldMeta('phoneCountryCode')}
                fieldCodeHelper={getFieldHelpers('phoneCountryCode')}
                fieldNumberProps={getFieldProps('phoneNumber')}
                fieldNumberMeta={getFieldMeta('phoneNumber')}
                placeholder="Enter Phone Number"
            />

            <FormItem style={{ textAlign: 'right', marginTop: '3.6rem' }}>
                <Button size="large" style={{ marginRight: '1.6rem' }} onClick={() => onCloseButton()}>
                    Cancel
                </Button>
                <Button disabled={!isValid} size="large" type="primary" onClick={handleOnPressButton} loading={loading}>
                    Confirm
                </Button>
            </FormItem>
        </Form>
    );
};
export default CreateNewIndividual;
